import React from "react";
import { Button, IconButton, Typography } from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import TextField from "@mui/material/TextField";
import { UsernameSelectField, useStyles } from "../../pages/Mint/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "../../components/LightToolTip/LightToolTip";
import { Collaborator } from "../../pages/Mint/types";
import { MintManageCreatorsProps } from "./types";
import { mintManageCreatorsStyles } from "./styles";
import { APP_BACKGROUND_COLOR, FONT_FAMILY, TEXT_COLOR } from "../../theme";

export const MintManageCreators: React.FC<MintManageCreatorsProps> = ({
  errors,
  touched,
  setShowPriceTooltip,
  showPriceTooltip,
  handleShowPriceTooltip,
  values,
  handleChange,
  handleBlur,
  isSubmitting,
  setShowCollabTooltip,
  showCollabTooltip,
  handleShowCollabTooltip,
  ownerShare,
  userName,
  collaborators,
  removeCollaborator,
  collabVal,
  setCollabVal,
  setFieldValue,
  newNFT,
  usersList,
  addCollaborator,
  setFieldError,
}) => {
  const classes = useStyles();
  return (
    <>
      <div className="input">
        <label
          style={{
            color: errors.price && touched.price ? "red" : TEXT_COLOR,
          }}
          htmlFor="price"
        >
          Price
          <div
            style={{ display: "inline" }}
            onMouseOver={() => setShowPriceTooltip(true)}
            onMouseLeave={() => setShowPriceTooltip(false)}
          >
            <LightTooltip
              title="If you would like to list your NFT for sale on OpenSea set a price in ethereum here. 
                        The price will be reflected on OpenSea within 24 hours. 
                        If you do not set a price the NFT will be open to offers on OpenSea"
              placement="top"
              open={showPriceTooltip}
              onClick={handleShowPriceTooltip}
            >
              <IconButton disableRipple>
                <HelpOutlineIcon
                  color="info"
                  style={{ height: 16, width: 16 }}
                />
              </IconButton>
            </LightTooltip>
          </div>
        </label>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <input
            style={{
              ...mintManageCreatorsStyles.priceInput,
              ...{
                borderColor: errors.price && touched.price ? "red" : "#cbd2e0",
              },
            }}
            className="input-price"
            type="number"
            name="price"
            id="price"
            min="0.01"
            step="0.000001"
            placeholder="NFT Price (ETH)"
            value={values.price}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />
          <img
            src="/assets/images/eth1.svg"
            alt="ethereum"
            style={mintManageCreatorsStyles.ethereumImage}
          />
        </div>
      </div>
      <div className="collab-label" style={{ color: TEXT_COLOR }}>
        <span>Add Creators </span>
        <span className="optional-input">(optional)</span>
        <div
          style={{ display: "inline" }}
          onMouseOver={() => setShowCollabTooltip(true)}
          onMouseLeave={() => setShowCollabTooltip(false)}
        >
          <LightTooltip
            title="Only add additional creators below if you would like to split the revenue of this 
                        NFT with other artists, producers, songwriters, or collaborators on OmMuse. 
                        If not, leave the owner share at 100%"
            placement="top"
            open={showCollabTooltip}
            onClick={handleShowCollabTooltip}
          >
            <IconButton disableRipple>
              <HelpOutlineIcon color="info" style={{ height: 16, width: 16 }} />
            </IconButton>
          </LightTooltip>
        </div>
      </div>
      <hr />
      <div className="inputs-group">
        <div style={{ color: TEXT_COLOR }} className="input">
          <label htmlFor="collaborator">Owner</label>
          <input
            type="text"
            name="owner"
            id="owner"
            disabled
            style={{ backgroundColor: "#61627A", color: TEXT_COLOR }}
            value={userName}
          />
        </div>
        <div style={{ color: TEXT_COLOR }} className="input">
          <label htmlFor="share">Share for the owner</label>
          <input
            type="text"
            name="share"
            id="share"
            disabled
            style={{ backgroundColor: "#61627A", color: TEXT_COLOR }}
            value={ownerShare + "%"}
          />
        </div>
      </div>
      <div className="collaborators">
        <div className="collaborators-target">
          {collaborators &&
            collaborators.length > 0 &&
            collaborators.map((collaborator: Collaborator, index: number) => (
              <div className="collaborator" key={index}>
                <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                  {collaborator.username}
                </p>
                <p style={{ fontFamily: FONT_FAMILY }}>{collaborator.share}%</p>
                {!isSubmitting && (
                  <IconButton onClick={() => removeCollaborator(collaborator)}>
                    <DeleteForeverIcon />
                  </IconButton>
                )}
              </div>
            ))}
        </div>
      </div>
      <div className="inputs-group">
        <div>
          <label
            htmlFor="newCollaboratorUser"
            style={mintManageCreatorsStyles.creatorUserNameLabel}
          >
            {"Creator's Username"}
          </label>
          <UsernameSelectField
            freeSolo
            forcePopupIcon={false}
            isOptionEqualToValue={(option, value) => option === value}
            inputValue={collabVal}
            onInputChange={(
              event: React.SyntheticEvent<Element, Event>,
              value: string
            ) => {
              setCollabVal(value);
              setFieldValue(
                "newCollaborator",
                value !== null
                  ? {
                      username: value,
                      share: "",
                    }
                  : newNFT.newCollaborator
              );
            }}
            options={usersList.sort()}
            renderInput={(params) => (
              <TextField
                style={{
                  backgroundColor: APP_BACKGROUND_COLOR,
                  color: TEXT_COLOR,
                }}
                {...params}
                label=" "
                InputLabelProps={{
                  shrink: false,
                }}
                classes={{ root: classes.customTextField }}
              />
            )}
            renderOption={(props, option: any) => (
              <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                {option}
              </li>
            )}
          />
        </div>
        <div className="input">
          <label style={{ color: TEXT_COLOR }} htmlFor="revenue_split">
            Split Revenue (%)
          </label>
          <div className="custom-mint-select">
            <input
              style={{
                backgroundColor: APP_BACKGROUND_COLOR,
                color: TEXT_COLOR,
              }}
              type="number"
              step="1"
              name="newCollaborator.share"
              id="newCollaborator.share"
              value={values.newCollaborator.share}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
              placeholder="5%"
              min="0"
              max="100"
            />
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row-reverse" }}>
        <Button
          fullWidth
          disabled={
            values.newCollaborator.username === "" ||
            values.newCollaborator.share === ""
          }
          sx={mintManageCreatorsStyles.addCreatorButton}
          size="small"
          variant="contained"
          onClick={() => {
            if (!isSubmitting) {
              addCollaborator(values.newCollaborator, setFieldError);
              values.newCollaborator = {
                username: "",
                share: "",
              };
            }
          }}
        >
          <Typography
            variant="h1"
            sx={mintManageCreatorsStyles.addCreatorTypography}
          >
            Add Creator
          </Typography>
        </Button>
      </div>
    </>
  );
};
